import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Icon from '../components/Icon';
import ApplicationTimeline from '../components/ApplicationTimeline';
import { contactInfo } from '../mock';

const getInitials = (name) =>
  name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

const InfoScreen = ({ iconName, title, description }) => (
  <div className="min-h-screen" style={{ background: 'var(--zg-bg)' }}>
    <div className="px-6" style={{ padding: '96px 24px 0', background: 'var(--zg-surface)' }}>
      <div className="mx-auto max-w-7xl">
        <Link to="/tracker" className="inline-flex items-center gap-2 no-underline" style={{ color: 'var(--zg-muted)' }}>
          <Icon name="arrowLeft" size={16} />
          <span>Back to Tracker</span>
        </Link>
      </div>
    </div>
    <section className="px-6 text-center" style={{ padding: '96px 24px' }}>
      <div className="mx-auto max-w-xl">
        <div
          className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full"
          style={{ background: 'var(--zg-surface2)', color: 'var(--zg-muted)' }}
        >
          <Icon name={iconName} size={28} />
        </div>
        <h1 className="text-3xl font-bold mb-3" style={{ color: 'var(--zg-ink)' }}>
          {title}
        </h1>
        <p className="mb-8" style={{ color: 'var(--zg-muted)' }}>
          {description}
        </p>
        <Link
          to="/tracker"
          className="inline-flex items-center h-11 px-6 rounded-full text-sm font-medium text-white no-underline"
          style={{ background: '#2d2a24' }}
        >
          Try Again
        </Link>
        <div className="mt-10 pt-8 border-t space-y-3" style={{ borderColor: 'var(--zg-border)' }}>
          <p className="text-sm" style={{ color: 'var(--zg-muted2)' }}>Still stuck? Our team can look it up for you.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${contactInfo.phone}`} className="font-medium no-underline" style={{ color: 'var(--zg-ink)' }}>
              {contactInfo.phone}
            </a>
            <span className="hidden sm:inline" style={{ color: '#b4ae9f' }}>|</span>
            <a href={`mailto:${contactInfo.email}`} className="font-medium no-underline" style={{ color: 'var(--zg-ink)' }}>
              {contactInfo.email}
            </a>
          </div>
        </div>
      </div>
    </section>
  </div>
);

const TrackApplicationStatus = () => {
  const [searchParams] = useSearchParams();
  const passportNumber = searchParams.get('passport') || '';
  const fileNumber = searchParams.get('file') || '';

  const [status, setStatus] = useState('loading'); // loading | ready | not_found | error
  const [data, setData] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    const controller = new AbortController();

    (async () => {
      try {
        const params = new URLSearchParams({ passport: passportNumber, file: fileNumber });
        const res = await fetch(`/api/applications/track?${params.toString()}`, {
          signal: controller.signal,
        });

        if (res.status === 404) {
          setStatus('not_found');
          return;
        }
        if (!res.ok) {
          setStatus('error');
          return;
        }

        const json = await res.json();
        setData(json);
        setStatus('ready');
        setTimeout(() => setMounted(true), 80);
      } catch (err) {
        if (err.name !== 'AbortError') setStatus('error');
      }
    })();

    return () => controller.abort();
  }, [passportNumber, fileNumber]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--zg-bg)' }}>
        <Icon name="timer" size={32} color="var(--zg-muted)" style={{ animation: 'pulse2 1.2s ease-in-out infinite' }} />
      </div>
    );
  }

  if (status === 'not_found') {
    return (
      <InfoScreen
        iconName="search"
        title="We Couldn't Find That Application"
        description={
          <>
            No application matches passport number{' '}
            <span className="font-mono font-semibold" style={{ color: 'var(--zg-ink)' }}>{passportNumber}</span> and file number{' '}
            <span className="font-mono font-semibold" style={{ color: 'var(--zg-ink)' }}>{fileNumber}</span>. Check both for typos
            and try again.
          </>
        }
      />
    );
  }

  if (status === 'error') {
    return (
      <InfoScreen
        iconName="info"
        title="Something Went Wrong"
        description="We couldn't load your application status right now. Please try again in a moment."
      />
    );
  }

  const stages = data.stages;
  const hasStarted = data.current_stage_index !== null;
  const progressPercent = hasStarted
    ? Math.round((data.current_stage_index / (stages.length - 1)) * 100)
    : 0;
  const currentStageLabel = hasStarted ? stages[data.current_stage_index].label : 'Awaiting update';

  return (
    <div className="min-h-screen" style={{ background: 'var(--zg-bg)' }}>
      <div className="px-6" style={{ padding: '96px 24px 0', background: 'var(--zg-surface)' }}>
        <div className="mx-auto max-w-7xl">
          <Link to="/tracker" className="inline-flex items-center gap-2 no-underline" style={{ color: 'var(--zg-muted)' }}>
            <Icon name="arrowLeft" size={16} />
            <span>Back to Tracker</span>
          </Link>
        </div>
      </div>

      <section className="px-6" style={{ padding: '32px 24px 64px', background: 'var(--zg-surface)' }}>
        <div className="mx-auto max-w-3xl">
          <span className="inline-block text-white text-xs rounded-full px-4 py-1.5 mb-6" style={{ background: '#2d2a24' }}>
            Application Status
          </span>

          <div
            className="rounded-[18px] border p-8"
            style={{
              borderColor: 'var(--zg-border)',
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(12px)',
              transition: 'opacity .5s ease, transform .5s ease',
            }}
          >
            <div className="flex items-center gap-4 mb-7">
              <div
                className="h-14 w-14 rounded-full text-white flex items-center justify-center text-lg font-bold flex-shrink-0"
                style={{ background: '#2d2a24' }}
              >
                {getInitials(data.full_name)}
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: 'var(--zg-ink)' }}>
                {data.full_name}
              </h1>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 pb-7 mb-7 border-b" style={{ borderColor: 'var(--zg-border)' }}>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-widest mb-1 flex items-center gap-1.5" style={{ color: 'var(--zg-muted2)' }}>
                  <Icon name="fileText" size={14} /> Passport No.
                </p>
                <p className="font-mono text-lg" style={{ color: 'var(--zg-ink)' }}>{data.passport_number}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-widest mb-1 flex items-center gap-1.5" style={{ color: 'var(--zg-muted2)' }}>
                  <Icon name="fileText" size={14} /> File No.
                </p>
                <p className="font-mono text-lg" style={{ color: 'var(--zg-ink)' }}>{data.file_number}</p>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-widest mb-1 flex items-center gap-1.5" style={{ color: 'var(--zg-muted2)' }}>
                  <Icon name="globe" size={14} /> Nationality
                </p>
                <p className="text-lg" style={{ color: 'var(--zg-ink)' }}>{data.nationality}</p>
              </div>
              {data.vendor && (
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-widest mb-1 flex items-center gap-1.5" style={{ color: 'var(--zg-muted2)' }}>
                    <Icon name="building" size={14} /> Partnered With
                  </p>
                  <p className="text-lg" style={{ color: 'var(--zg-ink)' }}>{data.vendor}</p>
                </div>
              )}
            </div>

            <div>
              <div className="flex justify-between mb-2 text-sm">
                <span className="font-medium" style={{ color: 'var(--zg-ink)' }}>{currentStageLabel}</span>
                <span style={{ color: 'var(--zg-muted2)' }}>{progressPercent}% complete</span>
              </div>
              <div className="h-2 w-full rounded-full overflow-hidden" style={{ background: 'var(--zg-border)' }}>
                <div
                  className="h-full rounded-full transition-all duration-700 ease-out"
                  style={{ width: mounted ? `${progressPercent}%` : '0%', background: '#e08e0b' }}
                />
              </div>
            </div>

            {data.notes && (
              <div
                className="mt-6 flex items-start gap-3 rounded-[18px] px-4 py-3.5"
                style={{ borderLeft: '4px solid var(--zg-logo-ink)', background: 'var(--zg-surface)' }}
              >
                <Icon name="info" size={20} color="var(--zg-ink)" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: 'var(--zg-muted2)' }}>Current Update</p>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--zg-ink)' }}>{data.notes}</p>
                  {data.updated_at && (
                    <p className="text-xs mt-1.5" style={{ color: 'var(--zg-muted2)' }}>
                      Last updated on {formatDate(data.updated_at)}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="px-6" style={{ padding: '80px 24px' }}>
        <div className="mx-auto max-w-3xl">
          <div className="mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: 'var(--zg-ink)' }}>Application Timeline</h2>
            <p style={{ color: 'var(--zg-muted)' }}>Every stage of your placement journey, from application to visa.</p>
          </div>

          <div className="rounded-[18px] border px-8 pt-8 pb-2" style={{ borderColor: 'var(--zg-border)' }}>
            <ApplicationTimeline stages={stages} />
          </div>

          <div className="mt-12 text-center space-y-4">
            <p className="flex items-center justify-center gap-2" style={{ color: 'var(--zg-muted)' }}>
              <Icon name="shield" size={16} />
              Status updates automatically as your application progresses.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrackApplicationStatus;
